// Thin client for the Horus API (docs/crm/api.md). Uses its own axios
// instance so the legacy interceptor in helpers/api_helper.js does not
// rewrite errors: callers get an ApiError with the HTTP status and the
// response body (409 bodies carry existing_id and field; 422 a code).
import axios from "axios";
import { api as apiConfig } from "../../config";
import { getToken, logout } from "./auth";
import { filenameFromDisposition } from "./reports";

const http = axios.create({ baseURL: apiConfig.API_URL });

http.interceptors.request.use((config) => {
    const token = getToken();
    if (token) config.headers.Authorization = "Bearer " + token;
    return config;
});

export class ApiError extends Error {
    constructor(message, status, data) {
        super(message);
        this.status = status;
        this.data = data || {};
    }
}

const toError = (err) => {
    if (err.response) {
        const { status, data } = err.response;
        if (status === 401 && getToken()) {
            logout("Session expired. Sign in again.");
        }
        let msg = (data && (data.error || data.message)) || (typeof data === "string" && data) || "";
        if (!msg) msg = status >= 500 ? "Server error." : `Request failed (${status}).`;
        return new ApiError(msg, status, data);
    }
    if (err.request) return new ApiError("Could not reach the server.", 0);
    return new ApiError(err.message || "Unknown error.", 0);
};

// Drop undefined, null and "" params so optional filters stay out of the URL.
export const cleanParams = (params = {}) => {
    const out = {};
    Object.keys(params).forEach((k) => {
        const v = params[k];
        if (v === undefined || v === null || v === "") return;
        out[k] = v;
    });
    return out;
};

const unwrap = (p) => p.then((r) => r.data).catch((e) => { throw toError(e); });

const path = (p) => (p.startsWith("/") ? p.slice(1) : p);

export const get = (p, params) => unwrap(http.get(path(p), { params: cleanParams(params) }));
export const post = (p, body) => unwrap(http.post(path(p), body));
export const put = (p, body) => unwrap(http.put(path(p), body));
export const patch = (p, body) => unwrap(http.patch(path(p), body));
export const del = (p) => unwrap(http.delete(path(p)));
export const upload = (p, formData, params) =>
    unwrap(http.post(path(p), formData, { params: cleanParams(params), headers: { "Content-Type": "multipart/form-data" } }));

// Authenticated file download (exports): fetches the body as a blob and
// saves it under the server's Content-Disposition name (or fallbackName).
// Error bodies of a blob request are JSON blobs; they are decoded so the
// caller still gets the API's error message.
export const download = async (p, params, fallbackName = "download") => {
    let res;
    try {
        res = await http.get(path(p), { params: cleanParams(params), responseType: "blob" });
    } catch (err) {
        if (err.response && err.response.data instanceof Blob) {
            try {
                err.response.data = JSON.parse(await err.response.data.text());
            } catch (e) {
                err.response.data = {};
            }
        }
        throw toError(err);
    }
    const name = filenameFromDisposition(res.headers["content-disposition"]) || fallbackName;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return name;
};

// Login is the only public route.
export const login = (username, password) => unwrap(http.post("v1/login", { username, password }));

const api = { get, post, put, patch, del, upload, download, login };
export default api;
